// Attachment preview — the lightbox a card opens into.
//
// Clicking a file on the Attachments section used to open the bytes in a
// new browser tab, which for a phone photo meant leaving the work item to
// look at it and coming back (2026-09-22). Now it opens HERE: a dark overlay
// over the app, the file rendered by what it is (image, PDF, video, audio;
// anything else gets its file tile and the download), the name and size in
// a header with Download / Open in new tab, ← → between the item's files,
// Esc / backdrop / × to close. Same auth'd content URLs the cards use, so
// nothing new is served.
//
// Documents (PDF, text) are the exception to "same URLs": an <iframe src>
// is a NAVIGATION, and the service worker answered navigations to /api/
// with the app shell — Stride booted inside the preview instead of the
// file (2026-09-30). The denylist in web/vite.config.js now excludes
// /api/, but a browser keeps its old worker until its next load, so a
// document is fetched as a Blob (loadBlob — a fetch is never a navigation)
// and the frame shows the blob: URL. The Blob's type is set HERE from the
// file's kind, never taken from the server: a PDF stored as
// application/octet-stream still draws, and a "text" file can never be
// rendered as HTML on our own origin.
//
// The caller owns which attachment is open, the URL builders and the
// loader; this component fetches only through loadBlob.

import React, { useEffect, useCallback, useState } from 'react';
import { createPortal } from 'react-dom';
import './AttachmentPreview.css';

const IMAGE_EXT = new Set(['png', 'jpg', 'jpeg', 'gif', 'svg', 'webp', 'bmp', 'avif']);
const VIDEO_EXT = new Set(['mp4', 'webm', 'mov', 'm4v', 'ogv']);
const AUDIO_EXT = new Set(['mp3', 'wav', 'flac', 'aac', 'ogg', 'm4a']);
const TEXT_EXT = new Set(['txt', 'log', 'md', 'csv', 'json', 'xml', 'yaml', 'yml']);

// What the browser can render inline for a given file name / mime type.
// Exported for the static suite.
export function previewKind(filename, mimeType) {
  const ext = String(filename || '').split('.').pop()?.toLowerCase() || '';
  const mime = String(mimeType || '').toLowerCase();
  if (mime.startsWith('image/') || IMAGE_EXT.has(ext)) return 'image';
  if (mime === 'application/pdf' || ext === 'pdf') return 'pdf';
  if (mime.startsWith('video/') || VIDEO_EXT.has(ext)) return 'video';
  if (mime.startsWith('audio/') || AUDIO_EXT.has(ext)) return 'audio';
  if (mime.startsWith('text/') || TEXT_EXT.has(ext)) return 'text';
  return 'none';
}

// Which files get a server-made thumbnail on their card. Mirrors thumbKind
// in server/attachment-thumb.js — a card never asks for one that cannot
// exist. Exported for the static suite.
const THUMB_IMAGE_EXT = new Set(['png', 'jpg', 'jpeg', 'gif', 'webp', 'avif', 'tif', 'tiff', 'svg']);
export function thumbKind(filename, mimeType) {
  const ext = String(filename || '').split('.').pop()?.toLowerCase() || '';
  const mime = String(mimeType || '').toLowerCase();
  if (mime === 'application/pdf' || ext === 'pdf') return 'pdf';
  if (THUMB_IMAGE_EXT.has(ext)) return 'image';
  if (mime.startsWith('image/') && THUMB_IMAGE_EXT.has(mime.slice(6).replace('+xml', ''))) return 'image';
  return null;
}

const BLOB_TYPE = { pdf: 'application/pdf', text: 'text/plain;charset=utf-8' };

// A document's bytes as a blob: URL the frame can show. { url, failed }.
function useDocumentBlob(att, kind, loadBlob) {
  const [state, setState] = useState({ url: null, failed: false });
  const wanted = !!att && !!loadBlob && !!BLOB_TYPE[kind];
  const id = att?.id;
  useEffect(() => {
    setState({ url: null, failed: false });
    if (!wanted) return undefined;
    let live = true;
    let made = null;
    loadBlob(att).then((blob) => {
      if (!live) return;
      made = URL.createObjectURL(new Blob([blob], { type: BLOB_TYPE[kind] }));
      setState({ url: made, failed: false });
    }).catch(() => { if (live) setState({ url: null, failed: true }); });
    return () => { live = false; if (made) URL.revokeObjectURL(made); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, kind, wanted]);
  return state;
}

function AttachmentPreview({
  attachments = [],
  index = 0,
  onClose,
  onNavigate,
  contentUrl,      // (attachment) => inline URL
  downloadUrl,     // (attachment) => attachment-disposition URL
  loadBlob,        // (attachment) => Promise<Blob> — documents (PDF, text)
  renderFileIcon,  // (filename, size) => node — the section's tile
  formatSize,      // (bytes) => string
  formatWhen,      // (iso) => string
}) {
  const att = attachments[index];
  const count = attachments.length;
  const hasPrev = index > 0;
  const hasNext = index < count - 1;

  const goPrev = useCallback(() => { if (hasPrev) onNavigate?.(index - 1); }, [hasPrev, index, onNavigate]);
  const goNext = useCallback(() => { if (hasNext) onNavigate?.(index + 1); }, [hasNext, index, onNavigate]);

  // Keyboard: Esc closes, arrows page. Scroll under the overlay is locked
  // while it is up so the wheel over a tall PDF never moves the panel.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); onClose?.(); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); goPrev(); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); goNext(); }
    };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose, goPrev, goNext]);

  const kind = att ? previewKind(att.filename, att.mimeType) : 'none';
  const doc = useDocumentBlob(att, kind, loadBlob);

  if (!att) return null;

  const src = contentUrl(att);
  const dl = downloadUrl(att);
  const viaBlob = !!loadBlob && !!BLOB_TYPE[kind];
  // What the frame (and "New tab") point at: the blob once it is here.
  const docSrc = viaBlob ? doc.url : src;

  const noPreview = (title) => (
    <div className="att-preview-none">
      {renderFileIcon?.(att.filename, 72)}
      <div className="att-preview-none-title">{title}</div>
      <a className="att-preview-btn att-preview-btn--primary" href={dl} download={att.filename}>Download {att.filename}</a>
    </div>
  );

  const body = (() => {
    switch (kind) {
      case 'image':
        return <img className="att-preview-image" src={src} alt={att.filename} />;
      case 'pdf':
        if (viaBlob && doc.failed) return noPreview('This file could not be loaded');
        if (!docSrc) return <div className="att-preview-loading">Loading…</div>;
        return <iframe className="att-preview-frame" src={docSrc} title={att.filename} />;
      case 'video':
        return <video className="att-preview-media" src={src} controls autoPlay />;
      case 'audio':
        return <audio className="att-preview-media att-preview-audio" src={src} controls autoPlay />;
      case 'text':
        if (viaBlob && doc.failed) return noPreview('This file could not be loaded');
        if (!docSrc) return <div className="att-preview-loading">Loading…</div>;
        return <iframe className="att-preview-frame att-preview-frame--text" src={docSrc} title={att.filename} />;
      default:
        return noPreview('No preview for this file type');
    }
  })();

  return createPortal(
    <div className="att-preview-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose?.(); }} role="dialog" aria-modal="true" aria-label={att.filename}>
      <div className="att-preview-header">
        <div className="att-preview-title">
          <span className="att-preview-name" title={att.filename}>{att.filename}</span>
          <span className="att-preview-meta">
            {formatSize?.(att.size)}
            {att.created && formatWhen && <> · {formatWhen(att.created)}</>}
            {count > 1 && <> · {index + 1} of {count}</>}
          </span>
        </div>
        <div className="att-preview-actions">
          <a className="att-preview-btn" href={dl} download={att.filename} title="Download">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 3v12" /><polyline points="6 11 12 17 18 11" /><path d="M4 21h16" /></svg>
            <span>Download</span>
          </a>
          <a className="att-preview-btn" href={docSrc || src} target="_blank" rel="noopener noreferrer" title="Open in a new tab">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /><polyline points="15 3 21 3 21 9" /><line x1="10" y1="14" x2="21" y2="3" /></svg>
            <span>New tab</span>
          </a>
          <button type="button" className="att-preview-btn att-preview-close" onClick={onClose} title="Close (Esc)" aria-label="Close">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
          </button>
        </div>
      </div>

      {hasPrev && (
        <button type="button" className="att-preview-nav att-preview-nav--prev" onClick={goPrev} title="Previous (←)" aria-label="Previous">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="15 18 9 12 15 6" /></svg>
        </button>
      )}
      {hasNext && (
        <button type="button" className="att-preview-nav att-preview-nav--next" onClick={goNext} title="Next (→)" aria-label="Next">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="9 18 15 12 9 6" /></svg>
        </button>
      )}

      <div className="att-preview-body" key={att.id}>
        {body}
      </div>
    </div>,
    document.body,
  );
}

export default AttachmentPreview;
